/*
 * @Description: 神策埋点统一上报工具集
 */

import { randomString } from '@/assets/js/utils';
import { syncSensorsIdentity } from '@/common/sensors-identity';
import { snowplowSetUserId, snowplowTrack } from '@/common/snowplow';
import { stapeTrack } from '@/common/stape';

// 定义事件参数类型
type EventParams = Record<string, unknown>;

type LocalUserInfo = {
  id?: number | string | null;
};

const PLATFORM_LANGUAGE_TEXT_MAP: Record<string, string> = {
  it: 'Italiano',
  pt: 'Português',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  en: 'English',
};

function getPlatformLanguageText(): string {
  try {
    const countryId = (localStorage.getItem('voo-country-id') || '').toLowerCase();
    return PLATFORM_LANGUAGE_TEXT_MAP[countryId] || 'English';
  } catch {
    return 'English';
  }
}

/**
 * 从埋点参数中读取有效事件 ID 字段，并统一转成字符串。
 * @param params 事件参数
 * @param key 字段名
 * @returns 有效事件 ID
 */
function getStringParam(params: EventParams, key: string): string {
  const value = params[key];
  if (typeof value === 'string' || typeof value === 'number') {
    return `${value}` || '';
  }
  return '';
}

/**
 * 解析神策与 Snowplow 共用的事件 ID。
 * @param params 事件参数
 * @returns 神策事件 ID
 */
function resolveSensorsEventId(params: EventParams): string {
  return (
    getStringParam(params, 'sensors_event_id') ||
    getStringParam(params, 'event_id') ||
    randomString()
  );
}

/**
 * 构建神策与 Snowplow 上报参数，避免继续透传 event_id。
 * @param params 过滤后的原始事件参数
 * @param sensorsEventId 神策事件 ID
 * @returns 神策与 Snowplow 上报参数
 */
function buildSensorsEventParams(params: EventParams, sensorsEventId: string): EventParams {
  const eventParams = { ...params };
  delete eventParams.event_id;
  return {
    ...eventParams,
    sensors_event_id: sensorsEventId,
  };
}

/**
 * 构建 Stape 上报参数，保留 event_id 供广告平台去重。
 * @param params 过滤后的原始事件参数
 * @param eventId 广告去重事件 ID
 * @returns Stape 上报参数
 */
function buildStapeEventParams(params: EventParams, eventId: string): EventParams {
  const eventParams = { ...params };
  delete eventParams.sensors_event_id;
  return {
    ...eventParams,
    event_id: eventId,
  };
}

/**
 * 从本地用户信息中读取 Snowplow 业务用户 ID。
 * @returns 用户 ID 字符串，无有效用户 ID 时返回空字符串
 */
function getSnowplowLocalUserId(): string {
  try {
    const raw = localStorage.getItem('voo-userInfo');
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return '';

    const userId = (parsed as LocalUserInfo).id;
    if (typeof userId !== 'string' && typeof userId !== 'number') return '';

    return `${userId}`.trim();
  } catch {
    return '';
  }
}

// 扩展 Window 接口
declare global {
  interface Window {
    vue_route?: {
      fullPath: string;
    };
  }
}

export const SDUtils = {
  /**
   * 同步神策实时身份到本地缓存，供 Snowplow 首屏和登录后事件兜底读取。
   */
  syncSensorsIdentity,

  /**
   * @desc 普通埋点上报
   * @param eventName 事件名称
   * @param params 事件参数
   * @param {boolean} [options.isSendFlag=true]
   *   true  → 同时上报神策 + sGTM（默认行为）
   *   false → 仅上报 sGTM，跳过神策（用于神策侧已下线的埋点，保留代码不删除）
   */
  sensorsTrack: (eventName: string, params: EventParams, { isSendFlag = true } = {}): void => {
    try {
      const _params: EventParams = {
        pub_platform_language: getPlatformLanguageText(),
        $url: window.vue_route
          ? `${window.location.origin}${window.vue_route?.fullPath}`
          : window.location.href,
      };
      // 根据标准：只要是值为 '', null, undefined, 'null', 'undefined'
      Object.keys(params).forEach((key: string) => {
        const v = params[key];
        if (
          v !== '' &&
          v !== 'null' &&
          v !== 'undefined' &&
          v !== null &&
          v !== undefined &&
          !(v instanceof Array && !v.length)
        ) {
          _params[key] = v;
        }
      });
      // 神策与 Snowplow 使用 sensors_event_id；Stape 仍保留 event_id 做广告去重。
      const sensorsEventId = resolveSensorsEventId(_params);
      const stapeEventId = getStringParam(_params, 'event_id') || sensorsEventId;
      const sensorsEventParams = buildSensorsEventParams(_params, sensorsEventId);
      const stapeEventParams = buildStapeEventParams(_params, stapeEventId);

      // 神策与 Snowplow 保持 1:1，isSendFlag = false 时两者都跳过。
      if (isSendFlag) {
        window.sensors?.track(eventName, sensorsEventParams);
        snowplowTrack(eventName, sensorsEventParams);
      }

      // sGTM 上报：始终执行，与 isSendFlag 无关
      stapeTrack(eventName, stapeEventParams);
    } catch (error) {
      console.log('sensors track error', error);
    }
  },

  /**
   * @desc 神策登录事件
   * @param uuid 用户唯一标识
   */
  login: (uuid: string | null | undefined): void => {
    try {
      if (uuid) {
        window.sensors?.login(uuid);
        const snowplowUserId = getSnowplowLocalUserId();
        if (snowplowUserId) {
          snowplowSetUserId(snowplowUserId);
        }
      } else {
        window.sensors?.logout();
      }
      syncSensorsIdentity();
    } catch (error) {
      console.log('sensors login error', error);
    }
  },
  /**
   * 神策埋点，用户登出事件
   */
  logout: (): void => {
    try {
      window.sensors?.logout();
      window.sensors?.setProfile({ pub_is_login: false });
      syncSensorsIdentity();
    } catch (error) {
      console.log('sensors logout error', error);
    }
  },
};
